import React from 'react';
import {
  DestaqueGridCard,
  DestaqueHeroCard,
  DestaqueTallCard,
  ROW_1_HEIGHT,
} from './DestaqueCard';
import { DestaquesMaisMobile } from './DestaquesMaisMobile';
import {
  layoutDestaques,
  toDestaqueCards,
  type DestaquesData,
  type NewsItem,
} from './helpers';

interface DestaquesGridProps {
  /** Itens curados no bloco `mpfDestaques`. */
  data?: DestaquesData;
  /** Últimas notícias do `@search`, usadas quando não há curadoria. */
  fallback?: NewsItem[];
  loading?: boolean;
  isEditMode?: boolean;
  /**
   * Presente só no editor (bloco `mpfDestaques`): recebe o índice do item em
   * `data.items`. A `CapaView` pública não passa, então o site não ganha
   * botão nenhum.
   */
  onEditItem?: (index: number) => void;
}

/**
 * Esqueleto exibido enquanto o `@search` não respondeu. Reserva a mesma
 * geometria da grade: sem ele a faixa cresce ~500px depois do primeiro paint
 * (o hook roda em `useEffect`, não no SSR) e empurra a página inteira.
 */
const DestaquesSkeleton: React.FC = () => (
  <div aria-busy="true" aria-live="polite">
    <span className="sr-only">Carregando destaques…</span>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div
        className={`aspect-[16/10] animate-pulse rounded-lg bg-gray-200 md:col-span-2 dark:bg-night-700 ${ROW_1_HEIGHT}`}
      />
      <div
        className={`hidden aspect-[4/3] animate-pulse rounded-lg bg-gray-200 md:block dark:bg-night-700 ${ROW_1_HEIGHT}`}
      />
    </div>
    <div className="hidden gap-4 md:mt-4 md:grid md:grid-cols-3">
      {[0, 1, 2].map((index) => (
        <div key={index}>
          <div className="aspect-[16/10] animate-pulse rounded-lg bg-gray-200 dark:bg-night-700" />
          <div className="mt-3 h-4 animate-pulse rounded bg-gray-200 dark:bg-night-700" />
        </div>
      ))}
    </div>
    {/* No mobile o lugar reservado é o da barra "Mais destaques" recolhida —
        56px, a altura do `<summary>` (16px de padding + 24px de conteúdo). */}
    <div className="mt-4 h-14 animate-pulse rounded-lg bg-gray-200 md:hidden dark:bg-night-700" />
  </div>
);

/**
 * Grade de destaques da capa (bloco `mpfDestaques`): manchete + card alto na
 * primeira linha, três cards na segunda.
 *
 * A curadoria do bloco tem prioridade; sem ela, as últimas notícias preenchem
 * as posições (ver `toDestaqueCards`).
 */
export const DestaquesGrid: React.FC<DestaquesGridProps> = ({
  data,
  fallback = [],
  loading,
  isEditMode,
  onEditItem,
}) => {
  const hasCurated = (data?.items?.length ?? 0) > 0;
  const cards = toDestaqueCards(data?.items, fallback);

  if (!hasCurated && loading && cards.length === 0) {
    return <DestaquesSkeleton />;
  }
  if (cards.length === 0) return null;

  const { hero, tall, cards: gridCards } = layoutDestaques(cards);
  // Tudo que não é a manchete: os cards do desktop e, no mobile, a lista de
  // texto do `DestaquesMaisMobile`.
  const secundarios = [...(tall ? [tall] : []), ...gridCards];

  return (
    /* `not-prose` porque a grade também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <div className="not-prose">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {hero && (
          <DestaqueHeroCard
            card={hero}
            isEditMode={isEditMode}
            onEditItem={onEditItem}
            // Sem card alto ao lado, a manchete ocupa a linha inteira em vez
            // de deixar uma coluna vazia.
            className={tall ? 'md:col-span-2' : 'md:col-span-3'}
          />
        )}
        {tall && (
          <DestaqueTallCard
            card={tall}
            isEditMode={isEditMode}
            onEditItem={onEditItem}
            className="hidden md:block"
          />
        )}
      </div>

      {gridCards.length > 0 && (
        <div className="hidden gap-4 md:mt-4 md:grid md:grid-cols-3">
          {gridCards.map((card) => (
            <DestaqueGridCard
              key={card.key}
              card={card}
              isEditMode={isEditMode}
              onEditItem={onEditItem}
            />
          ))}
        </div>
      )}

      {/* Abaixo de `md` os dois blocos acima somem (menos a manchete) e os
          destaques secundários reaparecem aqui, sem foto e recolhidos. */}
      <DestaquesMaisMobile cards={secundarios} isEditMode={isEditMode} />
    </div>
  );
};

export default DestaquesGrid;
